import type { ReactNode } from 'react';
import { Box, Button, IconButton, MenuItem, TextField, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { actionTone, ink, radius } from '@/theme/tokens';

/**
 * The list-page building blocks from the payroll wireframes, shared so every
 * page that adopts them looks the same: a compact stat strip, a plain table
 * with a grey uppercase header, soft pills, tinted row actions and a footer
 * pager. Sizes and colours come from theme/tokens, so a change there flows here.
 */

const R = radius.table;
const R_BTN = radius.button;
const R_PILL = radius.pill;

export type StripItem = {
  label: string;
  value: ReactNode;
  /** Value colour, e.g. red for an amount over budget. */
  tone?: 'default' | 'green' | 'amber' | 'red' | 'blue';
  /** Shown on hover instead of an extra line of text. */
  hint?: string;
  /** Makes the stat a shortcut (e.g. a count that filters the list). */
  onClick?: () => void;
};
const STRIP_TONE = { default: ink.stripVal, green: '#178a55', amber: '#b87b0a', red: '#e0614f', blue: '#2a64c8' };

/** "Total 2 | Active 2 | …" in one bordered row (wireframe .summary-strip). */
export function SummaryStrip({ items }: { items: StripItem[] }) {
  return (
    <Box sx={{
      display: 'inline-flex', flexWrap: 'wrap', maxWidth: '100%', mb: 2,
      border: `1px solid ${ink.border}`, borderRadius: R, bgcolor: '#fff', overflow: 'hidden' }}>
      {items.map((it) => {
        const cell = (
          // Each item draws its own left rule and is pulled 1px left, so the
          // first item on any row (after wrapping) tucks its rule under the edge.
          <Box key={it.label} onClick={it.onClick} role={it.onClick ? 'button' : undefined}
            tabIndex={it.onClick ? 0 : undefined}
            sx={{
              display: 'flex', alignItems: 'baseline', gap: 1, px: 2.25, py: 1.125,
              ml: '-1px', borderLeft: `1px solid ${ink.border}`,
              ...(it.onClick && { cursor: 'pointer', '&:hover': { bgcolor: '#f7fafd' } }) }}>
            <Box component="span" sx={{ fontSize: 13, color: 'text.secondary' }}>{it.label}</Box>
            <Box component="span" sx={{
              fontSize: 16, fontWeight: 700, color: STRIP_TONE[it.tone ?? 'default'], fontVariantNumeric: 'tabular-nums' }}>
              {it.value}
            </Box>
          </Box>
        );
        return it.hint ? <Tooltip key={it.label} title={it.hint}>{cell}</Tooltip> : cell;
      })}
    </Box>
  );
}

/** Styles for a raw <table>: header band, row rules, hover. */
export const kitTableSx = {
  width: '100%', borderCollapse: 'collapse',
  '& th': {
    bgcolor: ink.headBg, color: ink.headFg, fontSize: 11, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '.3px', textAlign: 'left',
    p: '12px 10px', borderBottom: `1px solid ${ink.border}`, whiteSpace: 'nowrap',
  },
  '& td': {
    p: '14px 10px', borderBottom: `1px solid ${ink.line}`, fontSize: 13.5,
    color: 'text.primary', verticalAlign: 'middle', bgcolor: '#fff',
  },
  '& th:first-of-type, & td:first-of-type': { pl: 2 },
  '& th:last-of-type, & td:last-of-type': { pr: 2 },
  '& .r': { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  '& .c': { textAlign: 'center' },
  '& tbody tr:last-of-type td': { borderBottom: 0 },
  '& tbody tr:hover td': { bgcolor: '#fafcff' },
} as const;

/** Bordered container a kit table and its footer sit in. */
export function TableCard({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ border: `1px solid ${ink.border}`, borderRadius: R, overflow: 'hidden', bgcolor: '#fff' }}>
      {children}
    </Box>
  );
}

const PILL = {
  grey: { bg: '#eef1f4', fg: '#5d6875', border: 'transparent' },
  green: { bg: '#e9fbf3', fg: '#0d9c64', border: '#c4ecd8' },
  muted: { bg: '#f5f6f8', fg: '#8a95a1', border: '#e1e5e8' },
  amber: { bg: '#fdf3e0', fg: '#8a5a10', border: '#f3dfb3' },
  red: { bg: '#fdecea', fg: '#c8392b', border: '#f6cdc7' },
  blue: { bg: '#e8f1fd', fg: '#2a64c8', border: '#cfe0f9' },
} as const;

/** A small soft label. `dot` adds the status dot ("• Active"). */
export function Pill({ children, tone = 'grey', dot }: {
  children: ReactNode; tone?: keyof typeof PILL; dot?: boolean;
}) {
  const c = PILL[tone];
  return (
    <Box component="span" sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.75, height: 22,
      px: dot ? 1.25 : 1, borderRadius: R_PILL, border: '1px solid', borderColor: c.border,
      bgcolor: c.bg, color: c.fg, fontSize: dot ? 12 : 11.5, fontWeight: dot ? 700 : 600,
      letterSpacing: '.2px', whiteSpace: 'nowrap' }}>
      {dot && <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: c.fg }} />}
      {children}
    </Box>
  );
}

const ACT = actionTone;

/** A tinted 28px row action: blue view/assign, cyan edit, red delete. */
export function ActionIcon({ tone, title, onClick, children, disabled }: {
  tone: keyof typeof ACT; title: string; onClick: () => void; children: ReactNode; disabled?: boolean;
}) {
  const c = ACT[tone];
  return (
    <Tooltip title={title}>
      <span>
        <IconButton aria-label={title} onClick={onClick} disabled={disabled} sx={{
          width: 28, height: 28, borderRadius: R, border: '1px solid', borderColor: c.fg,
          bgcolor: c.bg, color: c.fg, '& .MuiSvgIcon-root': { fontSize: 15 },
          '&:hover': { bgcolor: c.fg, color: '#fff' },
          '&.Mui-disabled': { opacity: 0.4, borderColor: c.fg, color: c.fg } }}>
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

export const TONE_BTN = {
  add: { bg: '#edfaf3', fg: '#1bcb84', border: '#1bcb84', hoverBg: '#e3f7ec', hoverFg: '#12a86a' },
  import: { bg: '#fff9ec', fg: '#b87b0a', border: '#f1b44c', hoverBg: '#fdf0d5', hoverFg: '#96630a' },
  blue: { bg: '#fff', fg: '#108ae6', border: '#108ae6', hoverBg: '#e8f4fd', hoverFg: '#0b6fbb' },
  export: { bg: '#fdecea', fg: '#e0614f', border: '#e0614f', hoverBg: '#fadcd8', hoverFg: '#c8392b' },
} as const;

/** A tinted outlined page button: green add, amber import, blue action, red export. */
export function ToneButton({ tone, icon, children, onClick, disabled }: {
  tone: keyof typeof TONE_BTN; icon?: ReactNode; children: ReactNode; onClick: () => void; disabled?: boolean;
}) {
  const c = TONE_BTN[tone];
  return (
    <Button variant="outlined" startIcon={icon} onClick={onClick} disabled={disabled}
      sx={{
        borderRadius: R_BTN, bgcolor: c.bg, color: c.fg, borderColor: c.border, whiteSpace: 'nowrap',
        '&:hover': { bgcolor: c.hoverBg, color: c.hoverFg, borderColor: c.hoverFg } }}>
      {children}
    </Button>
  );
}

/** The green "+ New …" page button. */
export function AddButton({ children, onClick, disabled }: { children: ReactNode; onClick: () => void; disabled?: boolean }) {
  return <ToneButton tone="add" icon={<AddIcon />} onClick={onClick} disabled={disabled}>{children}</ToneButton>;
}

/** "1–10 of 125" with previous / next. */
export function Pager({ from, to, total, page, lastPage, onPage, buttonsOnly }: {
  from: number; to: number; total: number; page: number; lastPage: number;
  onPage: (p: number) => void; buttonsOnly?: boolean;
}) {
  const btn = {
    width: 28, height: 28, border: '1px solid #e1e5e8', borderRadius: '6px',
    '&.Mui-disabled': { bgcolor: '#f7f9fb' },
  } as const;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5, textTransform: 'none',
      letterSpacing: 0, fontWeight: 500, fontSize: 12, color: 'text.secondary' }}>
      {!buttonsOnly && <Box component="span" sx={{ mr: 0.5, whiteSpace: 'nowrap' }}>{from}–{to} of {total}</Box>}
      <IconButton size="small" sx={btn} disabled={page <= 0} onClick={() => onPage(page - 1)} aria-label="Previous page">
        <ChevronLeftIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <IconButton size="small" sx={btn} disabled={page >= lastPage} onClick={() => onPage(page + 1)} aria-label="Next page">
        <ChevronRightIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}

/** Footer row: rows per page, range, optional note, and the pager. */
export function TableFooter({ total, page, pageSize, onPage, onPageSize, options = [10, 25, 50], label = 'Rows per page:', note, left }: {
  total: number; page: number; pageSize: number;
  onPage: (p: number) => void; onPageSize: (n: number) => void;
  options?: number[]; label?: string; note?: ReactNode;
  /** A summary on the far left; rows per page and the pager then sit together on the right. */
  left?: ReactNode;
}) {
  const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);
  const sizer = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
      <span>{label}</span>
      <TextField select size="small" fullWidth={false} value={pageSize}
        onChange={(e) => onPageSize(Number(e.target.value))}
        inputProps={{ 'aria-label': 'Rows per page' }}
        sx={{ width: 72 }}>
        {options.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
      </TextField>
      <span>{from}–{to} of {total}</span>
      {note && <Box component="span" sx={{ pl: 1.25, borderLeft: '1px solid #e1e5e8', fontSize: 12.5 }}>{note}</Box>}
    </Box>
  );
  const pager = <Pager from={from} to={to} total={total} page={page} lastPage={lastPage} onPage={onPage} buttonsOnly />;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5,
      flexWrap: 'wrap', px: 2, py: 1.5, borderTop: '1px solid #e1e5e8',
      fontSize: 13, color: 'text.secondary' }}>
      {left ? (
        <>
          <Box>{left}</Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>{sizer}{pager}</Box>
        </>
      ) : <>{sizer}{pager}</>}
    </Box>
  );
}
